import { Component, computed, effect, ElementRef, inject, input, resource, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { LayoutRowComponent } from '../controls/components/basic/layout-row/layout-row.component';
import { LayoutContainerComponent } from '../controls/components/basic/layout-container/layout-container.component';
import { FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import { LayoutColumnComponent } from '../controls/components/basic/layout-column/layout-column.component';
import { PartnerPortalService } from './services/partner-portal.service';
import { DocumentTypeOutput, Format, Identification, IdentificationType, ItemStatus, NewCommentEvent, PartnerContact, PartnerContactEmail, PartnerContactName, PartnerConversation, PartnerName, PartnerOnboarding, PartnerOnboardingItemStatus,  PartnerOnboardingOutput,  PartnerOnboardingStatus,  PartnerPhoneNumber, CredentialsStatus, TransportType, PartnerOnboardingData, PartnerOnboardingInput, DocumentDirection, ToggleConversationEvent, FormPartnerConversation } from './models/partner-onboarding';
import { ButtonComponent } from '../controls/components/basic/button/button.component';
import { ButtonPrimaryDirective } from '../controls/directives/button-primary.directive';

import { OnboardingDocumentsComponent } from './components/onboarding-documents/onboarding-documents.component';
import { SelectedDocumentType } from './components/onboarding-documents/models/document-type';
import { SelectedIdentification } from './components/onboarding-identifications/models/identification';
import { OnboardingIdentificationsComponent } from './components/onboarding-identifications/onboarding-identifications.component';
import { GenericModalService, ModalSettingsTemplate } from '../shared/generic-modal/generic-modal.service';
import { SendOnboardingDialogComponent, SendOnboardingDialogResult } from './components/send-onboarding-dialog/send-onboarding-dialog.component';
import { LoadingPanelService } from '../shared/loading-panel/loading-panel.service';
import { DxTabPanelModule, DxTextAreaModule } from 'devextreme-angular';
import { ToastService, ToastType } from '../shared/toast/toast.service';
import { OnboardingFieldComponent } from './components/onboarding-field/onboarding-field.component';
import { filter, interval, map, switchMap } from 'rxjs';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ButtonTertieryDirective } from '../controls/directives/button-tertiery.directive';
import { OnboardingStepperComponent } from './components/onboarding-stepper/onboarding-stepper.component';
import { LinkComponent } from '../controls/components/basic/link/link.component';
import { ImageDisplayComponent } from '../controls/components/basic/image-display/image-display.component';
import { CompanyProfile } from '../configuration/models/company-profile';

@Component({
    selector: 'app-partnerportal',
    standalone: true,
    imports: [
    DxTextAreaModule,
    FormsModule,
    ReactiveFormsModule,
    LayoutRowComponent,
    LayoutContainerComponent,
    LayoutColumnComponent,
    ButtonComponent,
    ButtonPrimaryDirective,
    ButtonTertieryDirective,
    OnboardingDocumentsComponent,
    OnboardingIdentificationsComponent,
    OnboardingFieldComponent,
    DxTabPanelModule,
    OnboardingStepperComponent,
    LinkComponent,
    ImageDisplayComponent
],
    templateUrl: './partnerportal.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './partnerportal.component.scss',
})
export class PartnerportalComponent {
    partnerId = input<number>();
    id = input<string>();
    form = viewChild<NgForm>('tForm');
    notes = viewChild<ElementRef>('notes');
    accessLogged = signal<boolean>(false);
    overrideUserProfile = signal<boolean>(false);
    autoRefreshConversations = signal<boolean>(false);
    currentStepId = signal<string>('step-company-name');
    pristineInitialized = signal<boolean>(false);
    initialFormValue = signal<unknown>(null);
    fieldStatusDict = signal<Record<string, string>>({});

    // Injected services
    modalService = inject(GenericModalService);
    loadingPanelService = inject(LoadingPanelService);
    partnerPortalService = inject(PartnerPortalService);
    toastService = inject(ToastService);

    DocumentDirection = DocumentDirection;

    constructor() {
        effect(() => {
            if (this.partnerOnboardingResource.isLoading()) {
                this.loadingPanelService.showLoadingPanelDirect('Loading...');
            } else {
                this.loadingPanelService.hideLoadingPanel();
            }
        });

        // When signals/resources have resolved, mark form pristine once
        effect(() => {
            const f = this.form()?.form;
            const loading =
                this.partnerOnboardingResource.isLoading() ||
                this.conversationResource.isLoading();
            const dataReady = !!this.model() && !!this.conversations();
            if (!f || loading || !dataReady || this.pristineInitialized())
                return;

            // Capture snapshot and mark pristine
            const value = structuredClone(f.getRawValue() as unknown);
            this.initialFormValue.set(value);
            f.markAsPristine();
            this.pristineInitialized.set(true);
        });

        interval(5000)
            .pipe(
                takeUntilDestroyed(),
                filter(() => this.autoRefreshConversations())
            )
            .subscribe(() => {
                this.conversationResource?.reload();
            });
    }

    // Resource and signals for conversations
    conversationResource = resource({
        params: () => ({
            partnerOnboardingGuid:
                this.partnerOnboarding()?.PartnerOnboardingGuid,
        }),
        loader: ({ params }) => this.getOnboardingConversations(params),
    });
    companyProfileResource = resource({
        params: () => ({
            partnerOnboardingGuid:
                this.partnerOnboarding()?.PartnerOnboardingGuid,
        }),
        loader: ({ params }) => this.getCompanyProfile(params)
    });

    showLogoOnPartnerPortal = computed(() => {
        const resource = this.companyProfileResource;
        if (resource.isLoading() || resource.error() || !resource.hasValue()) {
            return false;
        }

        const profile = resource.value();
        return !!profile?.Logo;
    });
    companyProfile = computed<CompanyProfile>(() => this.companyProfileResource.value());
    conversations = computed<PartnerConversation[]>(() => this.conversationResource.value());
    vissibleConversationId = signal<VissibleConversation>(null);
    visibleConversation = computed<FormPartnerConversation>(() => {
        if (!this.vissibleConversationId()) {
            return null;
        }

        const conversationId = this.vissibleConversationId()?.Id;
        const areaId = this.vissibleConversationId()?.AreaId;

        const conversations = this.conversations()?.filter(
            (c) => c.Id === conversationId
        );

        if (conversations?.length >= 1) {
            const conversation = conversations[0];
            return new FormPartnerConversation(conversation, areaId);
        } else {
            return new FormPartnerConversation(
                new PartnerConversation({
                    Id: conversationId,
                    Comments: [],
                    PartnerLastRead: null,
                    CustomerLastRead: null,
                }),
                areaId
            );
        }
    });
    unreadConversationIds = computed<string[]>(
        () =>
            this.conversations()
                ?.filter((c) => this.hasUnreadComments(c))
                .map((c) => c.Id) ?? []
    );

    // Resource and signals for partner onboarding data
    partnerOnboardingResource = resource({
        params: () => ({
            partnerOnboardingGuid: this.id(),
            partnerId: this.partnerId(),
        }),
        loader: ({ params }) => this.getOnboardingPartner(params),
    });
    partnerOnboarding = computed<PartnerOnboarding>(() => this.partnerOnboardingResource.value());
    partnerOnboardingData = computed<PartnerOnboardingData>(() => this.partnerOnboarding()?.Data);
    model = computed<PartnerOnboardingOutput>(() => this.partnerOnboardingData()?.Output);
    setup = computed<PartnerOnboardingInput>(() => this.partnerOnboardingData()?.Input);
    status = computed(
        () =>
            this.partnerOnboarding()?.Status ?? PartnerOnboardingStatus.Unknown
    );
    isOpenedByCustomer = computed(
        () =>
            this.partnerId() !== undefined &&
            this.partnerId() !== null &&
            this.partnerId() !== 0 &&
            !this.overrideUserProfile()
    );
    isFormOpen = computed(() => {
        const openIdentifications = this.model()?.Identifications.some(
            (i) => i.Status?.Status === ItemStatus.Open
        );
        const openDocumentTypesIn = this.model()?.DocumentTypesIn.some(
            (i) => i.Status?.Status === ItemStatus.Open
        );
        const openDocumentTypesOut = this.model()?.DocumentTypesOut.some(
            (i) => i.Status?.Status === ItemStatus.Open
        );
        const openName = this.model()?.Name.Status.Status === ItemStatus.Open;
        const openContactName =
            this.model()?.Contact.Name.Status.Status === ItemStatus.Open;
        const openContactEmail =
            this.model()?.Contact.Email.Status.Status === ItemStatus.Open;
        const openContactPhoneNumber =
            this.model()?.Contact.PhoneNumber.Status.Status === ItemStatus.Open;
        return (
            openIdentifications ||
            openDocumentTypesIn ||
            openDocumentTypesOut ||
            openName ||
            openContactName ||
            openContactEmail ||
            openContactPhoneNumber ||
            this.status() === PartnerOnboardingStatus.InProgress
        );
    });

    elementsAwaitingPartnerInput = computed(() => {
        const elements: unknown[] = [];
        if (this.model()) {
            if (this.model().Name.Status.Status === ItemStatus.Open) {
                elements.push({
                    ...this.model().Name,
                    stepId: 'step-company-name',
                });
            }
            if (this.model().Contact.Name.Status.Status === ItemStatus.Open) {
                elements.push({
                    ...this.model().Contact.Name,
                    stepId: 'step-contact',
                });
            }
            if (this.model().Contact.Email.Status.Status === ItemStatus.Open) {
                elements.push({
                    ...this.model().Contact.Email,
                    stepId: 'step-contact',
                });
            }
            if (
                this.model().Contact.PhoneNumber.Status.Status ===
                ItemStatus.Open
            ) {
                elements.push({
                    ...this.model().Contact.PhoneNumber,
                    stepId: 'step-contact',
                });
            }
            this.model()
                .Identifications?.filter(
                    (i) => i.Status.Status === ItemStatus.Open
                )
                .forEach((i) =>
                    elements.push({ ...i, stepId: 'step-identifications' })
                );
            this.model()
                .DocumentTypesIn?.filter(
                    (d) => d.Status.Status === ItemStatus.Open
                )
                .forEach((d) =>
                    elements.push({ ...d, stepId: 'step-document-in' })
                );
            this.model()
                .DocumentTypesOut?.filter(
                    (d) => d.Status.Status === ItemStatus.Open
                )
                .forEach((d) =>
                    elements.push({ ...d, stepId: 'step-document-out' })
                );
        }
        return elements;
    });

    elementsWithUnreadComments = computed(() => {
        const elements: unknown[] = [];
        const unreadIds = this.unreadConversationIds();
        if (this.model()) {
            if (unreadIds.includes(this.model().Name.ConversationId)) {
                elements.push({
                    ...this.model().Name,
                    stepId: 'step-company-name',
                });
            }
            if (unreadIds.includes(this.model().Contact.Name.ConversationId)) {
                elements.push({
                    ...this.model().Contact.Name,
                    stepId: 'step-contact',
                });
            }
            if (unreadIds.includes(this.model().Contact.Email.ConversationId)) {
                elements.push({
                    ...this.model().Contact.Email,
                    stepId: 'step-contact',
                });
            }
            if (
                unreadIds.includes(
                    this.model().Contact.PhoneNumber.ConversationId
                )
            ) {
                elements.push({
                    ...this.model().Contact.PhoneNumber,
                    stepId: 'step-contact',
                });
            }
            this.model()
                .Identifications?.filter((i) =>
                    unreadIds.includes(i.ConversationId)
                )
                .forEach((i) =>
                    elements.push({ ...i, stepId: 'step-identifications' })
                );
            this.model()
                .DocumentTypesIn?.filter((d) =>
                    unreadIds.includes(d.ConversationId) || d.Transport?.TransportFields.some((f) => unreadIds.includes(f.ConversationId))
                    ||
                    this.setup().SupportedTransports.find(s => s.Type === d.Transport?.Type)?.TransportFields.some((f) => unreadIds.includes(f.ConversationId))
                )
                .forEach((d) =>
                    elements.push({ ...d, stepId: 'step-document-in' })
                );
            this.model()
                .DocumentTypesOut?.filter((d) =>
                    unreadIds.includes(d.ConversationId) || d.Transport?.TransportFields.some((f) => unreadIds.includes(f.ConversationId))
                    ||
                    this.setup().SupportedTransports.find(s => s.Type === d.Transport?.Type)?.TransportFields.some((f) => unreadIds.includes(f.ConversationId))
                )
                .forEach((d) =>
                    elements.push({ ...d, stepId: 'step-document-out' })
                );

            this.setup()
                .SupportedTransports?.filter((t) => t.TransportFields.some((f) => unreadIds.includes(f.ConversationId)))
        }
        return elements;
    });

    statusIconClass: string = '';
    statusText = computed(() => {
        if (this.unsavedChanges()) {
            this.statusIconClass = 'dx-icon-info unread-comments';
            return 'You have unsaved changes';
        }
        if (this.unreadConversationIds()?.length > 0) {
            this.statusIconClass = 'dx-icon-info unread-comments';
            return `You have ${this.unreadConversationIds()?.length} unread ${
                this.unreadConversationIds()?.length > 1
                    ? 'messages'
                    : 'message'
            }`;
        }
        if (this.status() === PartnerOnboardingStatus.PartnerDone) {
            if (this.elementsAwaitingPartnerInput()?.length > 0) {
                this.statusIconClass = 'dx-icon-info awaiting-partner-input';
                return `You have ${
                    this.elementsAwaitingPartnerInput()?.length
                } field${
                    this.elementsAwaitingPartnerInput()?.length > 1 ? 's' : ''
                } awaiting input from you`;
            } else {
                this.statusIconClass = 'dx-icon-check partner-done';
                return 'Thank you for your submission. The onboarding is being reviewed by our team.';
            }
        } else if (this.status() === PartnerOnboardingStatus.Done) {
            this.statusIconClass = 'dx-icon-check done';
            return 'Thank you for your submission. The onboarding is complete.';
        } else {
            this.statusIconClass = 'dx-icon-info in-progress';
            return 'Remember to submit your onboarding when you are confident all information is provided.';
        }
    });

    // Unsaved changes detection
    currentValue = toSignal(
        toObservable(this.form).pipe(
            switchMap((f) => {
                const frm = f.form;
                return frm.valueChanges.pipe(
                    map(() => frm.getRawValue() as unknown)
                );
            })
        ),
        { initialValue: null }
    );
    unsavedChanges = computed(() => {
        if (!this.pristineInitialized()) {
            return false;
        }
        const changed =
            JSON.stringify(this.currentValue()) !==
            JSON.stringify(this.initialFormValue());
        return changed;
    });

    // Stepper logic
    sections = [
        {
            id: 'step-company-name',
            name: 'Please Provide a Company Name',
        },
        {
            id: 'step-contact',
            name: 'How Can We Contact You?',
        },
        {
            id: 'step-identifications',
            name: 'We Need an Identifier',
        },
        {
            id: 'step-document-in',
            name: 'What kinds of documents would you like to send to us?',
        },
        {
            id: 'step-document-out',
            name: 'What kinds of documents would you like to receive from us?',
        },
        {
            id: 'step-anything-else',
            name: 'Anything else?',
        },
    ];

    steps = computed(() =>
        this.sections.map(
            (section) =>
                ({
                    id: section.id,
                    name: section.name,
                    isCompleted: this.isStepCompleted(section.id),
                    errorText: this.getErrorTextForStep(section.id),
                    warningText: this.getWarningTextForStep(section.id),
                    infoText: '',
                })
        )
    );

    currentStepNumber = computed(() => {
        const list = this.steps() ?? [];
        const idx = list.findIndex((s) => s.id === this.currentStepId());
        return idx + 1;
    });

    totalSteps = computed(() => {
        const list = this.steps() ?? [];
        return list.length;
    });

    prevDisabled = computed(() => {
        const list = this.steps() ?? [];
        const idx = list.findIndex((s) => s.id === this.currentStepId());
        return idx <= 0;
    });

    nextDisabled = computed(() => {
        const list = this.steps() ?? [];
        const idx = list.findIndex((s) => s.id === this.currentStepId());
        return idx === -1 || idx >= list.length - 1;
    });

    isCompanySectionValid = computed(() => {
        const companyStatus = this.fieldStatusDict()['companyName'] ?? '';
        return companyStatus.toLowerCase() !== 'invalid';
    });

    isContactSectionValid = computed(() => {
        const nameStatus = this.fieldStatusDict()['contactName'] ?? '';
        const emailStatus = this.fieldStatusDict()['contactEmail'] ?? '';
        const phoneStatus = this.fieldStatusDict()['contactPhone'] ?? '';
        return (
            nameStatus.toLowerCase() !== 'invalid' &&
            emailStatus.toLowerCase() !== 'invalid' &&
            phoneStatus.toLowerCase() !== 'invalid'
        );
    });

    isIdentificationsSectionValid = computed(() => {
        const identificationsStatus =
            this.fieldStatusDict()['identifications'] ?? '';
        return identificationsStatus.toLowerCase() !== 'invalid';
    });

    isDocumentsInSectionValid = computed(() => {
        const documentsStatus = this.fieldStatusDict()['documentTypesIn'] ?? '';
        return documentsStatus.toLowerCase() !== 'invalid';
    });

    isDocumentsOutSectionValid = computed(() => {
        const documentsStatus =
            this.fieldStatusDict()['documentTypesOut'] ?? '';
        return documentsStatus.toLowerCase() !== 'invalid';
    });

    goToNextStep() {
        const list = this.steps() ?? [];
        const idx = list.findIndex((s) => s.id === this.currentStepId());
        if (idx > -1 && idx < list.length - 1) {
            this.goToStep(list[idx + 1].id);
        }
    }

    goToPrevStep() {
        const list = this.steps() ?? [];
        const idx = list.findIndex((s) => s.id === this.currentStepId());
        if (idx > 0) {
            this.goToStep(list[idx - 1].id);
        }
    }

    goToStep(stepId: string) {
        this.currentStepId.set(stepId);
        this.vissibleConversationId.set(null);
    }

    isStepCompleted(stepId: string) {
        const fieldsAwaitningInputInStep =
            this.elementsAwaitingPartnerInput()?.filter(
                (e) => e['stepId'] === stepId
            );
        if (
            (this.status() === PartnerOnboardingStatus.PartnerDone ||
                this.status() === PartnerOnboardingStatus.Done) &&
            fieldsAwaitningInputInStep.length === 0
        ) {
            return true;
        }
        return false;
    }

    getErrorTextForStep(stepId: string): string {
        if (this.partnerOnboardingResource.isLoading()) return null;
        if (
            stepId === 'step-company-name' &&
            this.isCompanySectionValid() === false
        ) {
            return 'Required';
        }
        if (
            stepId === 'step-contact' &&
            this.isContactSectionValid() === false
        ) {
            return 'Required';
        }
        if (
            stepId === 'step-identifications' &&
            this.isIdentificationsSectionValid() === false
        ) {
            return 'Required';
        }
        if (
            stepId === 'step-document-in' &&
            this.isDocumentsInSectionValid() === false
        ) {
            return 'Required';
        }
        if (
            stepId === 'step-document-out' &&
            this.isDocumentsOutSectionValid() === false
        ) {
            return 'Required';
        }
        return '';
    }

    getWarningTextForStep(stepId: string): string {
        if (
            this.partnerOnboardingResource.isLoading() ||
            this.conversationResource.isLoading()
        )
            return null;
        const fieldsWithUnreadComments =
            this.elementsWithUnreadComments()?.filter(
                (e) => e['stepId'] === stepId
            );
        if (fieldsWithUnreadComments.length > 0) {
            return `You have ${fieldsWithUnreadComments.length} field${
                fieldsWithUnreadComments.length > 1 ? 's' : ''
            } with unread messages`;
        }
        return '';
    }

    onFormStatusChanged(status: string, field: string) {
        this.fieldStatusDict.update((dict) => ({ ...dict, [field]: status }));
    }

    //Get methods for resource loading
    async getOnboardingPartner(request: {
        partnerOnboardingGuid: string;
        partnerId: number;
    }): Promise<PartnerOnboarding> {
        if (request.partnerOnboardingGuid || request.partnerId) {
            console.log('Loading onboarding partner');
            const isOpenedByCustomer =
                request.partnerId !== undefined &&
                request.partnerId !== null &&
                request.partnerId !== 0;
            const onboardingPartner =
                await this.partnerPortalService.getOnboardingPartner(
                    request.partnerOnboardingGuid,
                    request.partnerId
                );

            if (onboardingPartner && !this.accessLogged) {
                await this.partnerPortalService.logAccess(
                    onboardingPartner.PartnerOnboardingGuid,
                    isOpenedByCustomer
                );
                this.accessLogged.set(true);
            }

            // Reset pristine flag to allow re-initialization
            this.pristineInitialized.set(false);

            return onboardingPartner;
        } else {
            return null;
        }
    }

    async getOnboardingConversations(request: {
        partnerOnboardingGuid: string;
    }): Promise<PartnerConversation[]> {
        if (request.partnerOnboardingGuid) {
            const conversations =
                await this.partnerPortalService.getConversations(
                    request.partnerOnboardingGuid
                );

            // Find the visible conversation and mark it as read if it has unread comments
            const vc = conversations.find(
                c => c.Id === this.vissibleConversationId()?.Id
            );
            if (vc && this.hasUnreadComments(vc)) {
                vc.PartnerLastRead = new Date();
                void this.partnerPortalService.setConversationReadByPartner(this.vissibleConversationId()?.Id);
            }
            return conversations;
        } else {
            return [];
        }
    }

    async getCompanyProfile(request: {
        partnerOnboardingGuid: string;
    }): Promise<CompanyProfile> {
        if (request.partnerOnboardingGuid) {
            const companyProfile = await this.partnerPortalService.getGetCompanyProfile(
                request.partnerOnboardingGuid
            );
            return companyProfile;
        }
        return null;
    }

    // Show save dialog
    async showSendDialog() {
        const modalSettings = new ModalSettingsTemplate('lg', 'static');
        await this.modalService.openComponentWithInitialize<
            SendOnboardingDialogResult,
            SendOnboardingDialogComponent
        >(SendOnboardingDialogComponent, modalSettings, (dialog) => {
            dialog.isUpdate.set(
                this.status() === PartnerOnboardingStatus.PartnerDone
            );
        });

        await this.save(true);
        this.partnerOnboardingResource.reload();
    }

    scrollToTop(): void {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    switchUserProfile(): void {
        this.overrideUserProfile.set(!this.overrideUserProfile());
    }

    // Get current form values and save
    async save(markAsDone: boolean): Promise<PartnerOnboarding> {
        console.log(this.form().value);
        const fPartnerName = this.getPartnerName();
        const fContact = this.getPartnerContact();
        const fNote = this.form().form.get('note')?.getRawValue() as string;
        const fIdentifications = this.getSelectedIdentifications();
        const fDocumantTypesIn = this.getSelectedDocumentTypesIn();
        const fDocumentTypesOut = this.getSelectedDocumentTypesOut();

        const data = new PartnerOnboardingOutput({
            Name: fPartnerName,
            Contact: fContact,
            Note: fNote,
            Identifications: fIdentifications,
            DocumentTypesIn: fDocumantTypesIn,
            DocumentTypesOut: fDocumentTypesOut,
        });

        let partnerOnboarding = new PartnerOnboarding(this.partnerOnboarding());
        partnerOnboarding.Data.Output = data;
        partnerOnboarding.Status = markAsDone
            ? PartnerOnboardingStatus.PartnerDone
            : partnerOnboarding.Status;

        if (markAsDone) {
            partnerOnboarding = this.updateItemStatus(partnerOnboarding);
        }
        const savedPartnerOnboarding =
            await this.partnerPortalService.saveOnboardingPartner(
                partnerOnboarding,
                markAsDone
            );

        this.partnerOnboardingResource.reload();
        this.toastService.showToast(
            'Onboarding data saved.',
            ToastType.success
        );
        this.scrollToTop();
        return savedPartnerOnboarding;
    }

    updateItemStatus(partnerOnboarding: PartnerOnboarding) {
        const identifications =
            partnerOnboarding.Data.Output.Identifications.map((i) => {
                if (i.Status?.Status !== ItemStatus.Accepted) {
                    i.Status = {
                        Status: ItemStatus.AwaitingAcceptance,
                        CreatedEntities: i.Status?.CreatedEntities ?? [],
                    };
                }
                return i;
            });

        const documentTypesIn =
            partnerOnboarding.Data.Output.DocumentTypesIn.map((i) => {
                if (i.Status?.Status !== ItemStatus.Accepted) {
                    i.Status = {
                        Status: ItemStatus.AwaitingAcceptance,
                        CreatedEntities: i.Status?.CreatedEntities ?? [],
                    };
                }
                return i;
            });

        const documentTypesOut =
            partnerOnboarding.Data.Output.DocumentTypesOut.map((i) => {
                if (i.Status?.Status !== ItemStatus.Accepted) {
                    i.Status = {
                        Status: ItemStatus.AwaitingAcceptance,
                        CreatedEntities: i.Status?.CreatedEntities ?? [],
                    };
                }
                return i;
            });

        const partnerName = partnerOnboarding.Data.Output.Name;
        if (partnerName.Status?.Status !== ItemStatus.Accepted) {
            partnerName.Status = {
                Status: ItemStatus.AwaitingAcceptance,
                CreatedEntities: partnerName.Status?.CreatedEntities ?? [],
            };
        }

        const partnerContactName = partnerOnboarding.Data.Output.Contact.Name;
        if (partnerContactName.Status?.Status !== ItemStatus.Accepted) {
            partnerContactName.Status = {
                Status: ItemStatus.AwaitingAcceptance,
                CreatedEntities:
                    partnerContactName.Status?.CreatedEntities ?? [],
            };
        }

        const partnerContactEmail = partnerOnboarding.Data.Output.Contact.Email;
        if (partnerContactEmail.Status?.Status !== ItemStatus.Accepted) {
            partnerContactEmail.Status = {
                Status: ItemStatus.AwaitingAcceptance,
                CreatedEntities:
                    partnerContactEmail.Status?.CreatedEntities ?? [],
            };
        }

        const partnerPhoneNumber =
            partnerOnboarding.Data.Output.Contact.PhoneNumber;
        if (partnerPhoneNumber.Status?.Status !== ItemStatus.Accepted) {
            partnerPhoneNumber.Status = {
                Status: ItemStatus.AwaitingAcceptance,
                CreatedEntities:
                    partnerPhoneNumber.Status?.CreatedEntities ?? [],
            };
        }

        const partnerContact = new PartnerContact({
            Name: partnerContactName,
            Email: partnerContactEmail,
            PhoneNumber: partnerPhoneNumber,
            Types: partnerOnboarding.Data.Output.Contact.Types,
        });

        partnerOnboarding.Data.Output.Identifications = identifications;
        partnerOnboarding.Data.Output.DocumentTypesIn = documentTypesIn;
        partnerOnboarding.Data.Output.DocumentTypesOut = documentTypesOut;
        partnerOnboarding.Data.Output.Name = partnerName;
        partnerOnboarding.Data.Output.Contact = partnerContact;
        return partnerOnboarding;
    }

  requestCredentials(transportType: TransportType) {
    if (this.unsavedChanges()) {
      void this.modalService.messageDirect('Request Credentials', 'Please save your changes before requesting credentials.').then(() => undefined);
      return;
    }
    void this.partnerPortalService.requestCredentials(this.partnerOnboarding().PartnerOnboardingGuid, transportType).then((status) => {
      if (status === CredentialsStatus.Provided) {
        this.partnerOnboardingResource.reload();
        this.toastService.showToast('Credentials received.', ToastType.success);
      }
      else if (status === CredentialsStatus.Requested) {
        this.partnerOnboardingResource.reload();
        this.toastService.showToast('Credentials requested successfully.', ToastType.success);
      }
      else {
        this.toastService.showToast('Failed to request credentials.', ToastType.error);
      }
    });
  }

  async showCredentials(transportType: TransportType) {
    const credentials = await this.partnerPortalService.getCredentials(this.partnerOnboarding().PartnerOnboardingGuid, transportType);
    if (credentials) {
      await this.modalService.messageDirect('Transport Credentials', '<b>Username</b>: ' + credentials.Username + '<br/><b>Password:</b> ' + credentials.Password);
    }
    else {
        this.toastService.showToast('Failed to retrieve credentials.', ToastType.error);
    }
  }

    // Helpers for getting selected values from form
    getSelectedIdentifications() {
        const selectedIdentifications = this.form()
            .form.get('identifications')
            ?.getRawValue() as SelectedIdentification[];
        return selectedIdentifications.map(
            (i) =>
                new Identification({
                    IdentificationType: new IdentificationType({
                        Id: i.IdentificationType.Id,
                        Name: i.IdentificationType.Name,
                    }),
                    Value: i.Value,
                    Status: i.Status,
                    ConversationId: i.ConversationId ?? crypto.randomUUID(),
                })
        );
    }

  getSelectedDocumentTypesIn() {
    const selectedDocumentTypesIn = this.form().form.get('documentTypesIn')?.getRawValue() as SelectedDocumentType[];
    return selectedDocumentTypesIn.map(i => new DocumentTypeOutput({Id: i.Id, Name: i.Name, Format: new Format({Id: i.Format.Id, Name: i.Format.Name}), Transport: i.Transport, Status: i.Status, ConversationId: i.ConversationId ?? crypto.randomUUID()}));
  }

  getSelectedDocumentTypesOut() {
    const selectedDocumentTypesOut = this.form().form.get('documentTypesOut')?.getRawValue() as SelectedDocumentType[];
    return selectedDocumentTypesOut.map(i => new DocumentTypeOutput({Id: i.Id, Name: i.Name, Format: new Format({Id: i.Format.Id, Name: i.Format.Name}), Transport: i.Transport, Status: i.Status, ConversationId: i.ConversationId ?? crypto.randomUUID()}));
  }

    getPartnerName() {
        const partnerName = this.form()
            .form.get('name')
            ?.getRawValue() as PartnerName;
        partnerName.ConversationId =
            partnerName.ConversationId ?? crypto.randomUUID();
        return partnerName;
    }

    getPartnerContact() {
        const partnerContactName = this.form()
            .form.get('contactName')
            ?.getRawValue() as PartnerContactName;
        partnerContactName.ConversationId =
            partnerContactName.ConversationId ?? crypto.randomUUID();
        const partnerPhoneNumber = this.form()
            .form.get('contactPhone')
            ?.getRawValue() as PartnerPhoneNumber;
        partnerPhoneNumber.ConversationId =
            partnerPhoneNumber.ConversationId ?? crypto.randomUUID();
        const partnerContactEmail = this.form()
            .form.get('contactEmail')
            ?.getRawValue() as PartnerContactEmail;
        partnerContactEmail.ConversationId =
            partnerContactEmail.ConversationId ?? crypto.randomUUID();
        return new PartnerContact({
            Name: partnerContactName,
            Email: partnerContactEmail,
            PhoneNumber: partnerPhoneNumber,
            Types: this.model()?.Contact.Types ?? [],
        });
    }

    isStatusReadOnly(status: PartnerOnboardingItemStatus) {
        return (
            status?.Status === ItemStatus.Accepted ||
            status?.Status === ItemStatus.AwaitingAcceptance
        );
    }

    // Handlers for conversations
    async onToggleConversation(event: ToggleConversationEvent) {
        const conversationId = event.ConversationId;
        const areaId = event.AreaId;

        if (conversationId !== null) {
            const vc = new VissibleConversation(conversationId, areaId);

            this.vissibleConversationId.set(
                this.vissibleConversationId()?.Id === vc.Id &&
                this.vissibleConversationId()?.AreaId === vc.AreaId
                    ? null
                    : vc
            );
            if (this.vissibleConversationId() !== null && !this.overrideUserProfile()) {
                await this.partnerPortalService
                    .setConversationReadByPartner(conversationId)
                    .then(() => {
                        this.conversationResource.reload();
                    });
            }
        } else {
            await this.modalService.messageDirect(
                'Start new conversation',
                'Please save your changes before starting a new conversation.'
            );
        }
    }

    onToggleAutoRefreshConversations() {
        this.autoRefreshConversations.set(!this.autoRefreshConversations());
    }

    async onNewComment(event: NewCommentEvent) {
        await this.partnerPortalService
            .postComment(
                event.Comment,
                event.ConversationId,
                this.partnerOnboarding().PartnerOnboardingGuid
            )
            .then((conversation) => {
                if (conversation) {
                    this.conversationResource.reload();
                }
            });
    }

    hasUnreadComments(conversation: PartnerConversation): boolean {
        if (!conversation.PartnerLastRead)
            return (
                (conversation.Comments?.filter((c) => !c.IsAction).length ??
                    0) > 0
            );
        return conversation.Comments?.some(
            (c) =>
                c.FromCustomer &&
                c.IsAction === false &&
                c.Timestamp > conversation.PartnerLastRead
        );
    }
}

class VissibleConversation {
    Id: string
    AreaId: string | null
    constructor(id: string, areaId: string | null) {
        this.Id = id;
        this.AreaId = areaId;
    }
}